/**
 * Path Utility Functions for Cloud Files
 *
 * Utilities for parsing, building, and navigating cloud file paths.
 */

/**
 * Split a cloud file path into segments.
 * @param path - Full path, e.g., "/Documents/Reports/2024/sales.pdf"
 * @returns Array of path segments
 */
export const splitPath = (path: string): string[] => {
  return path.split("/").filter(Boolean);
};

/**
 * Build a path from segments up to a specific index.
 * @param segments - Array of path segments
 * @param toIndex - Index to build up to (inclusive)
 * @returns Path string
 */
export const buildPathToIndex = (
  segments: string[],
  toIndex: number,
): string => {
  return "/" + segments.slice(0, toIndex + 1).join("/");
};

/**
 * Get the parent path of a given path.
 * @param path - Current path
 * @returns Parent path, or "/" if already at root
 */
export const getParentPath = (path: string): string => {
  const segments = splitPath(path);
  if (segments.length <= 1) return "/";
  return "/" + segments.slice(0, -1).join("/");
};

/**
 * Check if a path is the root path.
 */
export const isRootPath = (path: string): boolean => {
  return path === "/" || path === "";
};

/**
 * Get the depth of a path (number of segments).
 */
export const getPathDepth = (path: string): number => {
  return splitPath(path).length;
};

/**
 * Check if childPath is a descendant of parentPath.
 */
export const isDescendantOf = (
  childPath: string,
  parentPath: string,
): boolean => {
  if (isRootPath(parentPath)) return true;
  const normalizedParent = parentPath.endsWith("/")
    ? parentPath
    : parentPath + "/";
  return childPath.startsWith(normalizedParent);
};
